<template>
  <div>
    <LoadingSpinner v-if="loading" message="Loading execution history..." size="small" />

    <div v-else-if="error" class="error-message">{{ error }}</div>

    <div v-else-if="executions.length === 0" class="empty-history">
      No executions yet. Click "Execute" to run this pipeline.
    </div>

    <div v-else class="executions-list">
      <div
        v-for="execution in executions"
        :key="execution.id"
        class="execution-item"
      >
        <div class="execution-header">
          <div class="execution-time">
            <Clock :size="14" />
            <span>{{ formatDateTime(execution.startedAt) }}</span>
          </div>
          <div class="execution-actions">
            <span :class="['status-badge', `status-${execution.status?.toLowerCase()}`]">
              {{ execution.status }}
            </span>
            <button
              @click.stop="$emit('refresh', execution.id)"
              class="btn-icon"
              title="Refresh status"
            >
              <RefreshCw :size="14" />
            </button>
          </div>
        </div>

        <div class="execution-details">
          <div class="detail-item">
            <strong>Duration:</strong>
            <span>{{ calculateDuration(execution) }}</span>
          </div>
          <div class="detail-item">
            <strong>Records:</strong>
            <span>{{ execution.processedRows || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <strong>Destination:</strong>
            <span>{{ execution.destinationTable || 'N/A' }}</span>
          </div>
          <div v-if="execution.executedBy" class="detail-item">
            <strong>Executed By:</strong>
            <span>{{ execution.executedBy }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Clock, RefreshCw } from 'lucide-vue-next'
import LoadingSpinner from '@/Modules/ETL/components/common/LoadingSpinner.vue'

defineProps({
  executions: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
})

defineEmits(['refresh'])

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString()
}

const calculateDuration = (execution) => {
  if (!execution.startedAt) return 'N/A'
  const start = new Date(execution.startedAt)
  const end = execution.completedAt ? new Date(execution.completedAt) : new Date()
  const totalSeconds = Math.floor((end - start) / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}m ${seconds}s`
}
</script>

<style scoped>
.executions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 500px;
  overflow-y: auto;
}

.execution-item {
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 1rem;
  transition: all 0.2s ease;
}

.execution-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.execution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}

.execution-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.execution-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.execution-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item strong {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: var(--color-text-primary);
  word-break: break-word;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #f1f5f9;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: var(--color-accent);
  color: white;
}

.empty-history {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 14px;
  background: white;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
}

.error-message {
  padding: 1rem 1.25rem;
  background: var(--color-error-light);
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: var(--color-error);
  font-size: 14px;
}
</style>
